import type { RoleSystemAnnouncement } from '../types/roleNotification'
import { isAnnouncementShown } from '../types/roleNotification'

/** Session-only set of sa_ids the user fully acknowledged ("Okay, I've Got It"). */
const ACK_SESSION_KEY = 'system_announcement_acked_ids'

function readAckedIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(ACK_SESSION_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(
      parsed
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0),
    )
  } catch {
    return new Set()
  }
}

function writeAckedIds(ids: Set<number>): void {
  try {
    sessionStorage.setItem(ACK_SESSION_KEY, JSON.stringify([...ids]))
  } catch {
    /* ignore */
  }
}

/** Mark announcement as permanently acknowledged for this browser session. */
export function markAnnouncementAcknowledgedInSession(saId: number): void {
  const ids = readAckedIds()
  ids.add(saId)
  writeAckedIds(ids)
}

export function isAnnouncementAcknowledgedInSession(saId: number): boolean {
  return readAckedIds().has(saId)
}

/** Clear session acks on logout so a new login can show announcements again. */
export function clearAnnouncementAcknowledgedSession(): void {
  try {
    sessionStorage.removeItem(ACK_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

/** True when popup should not open for this item (opened this session or fully ack'd). */
export function shouldSuppressAnnouncementPopup(
  announcement: RoleSystemAnnouncement,
): boolean {
  if (isAnnouncementShown(announcement)) return true
  if (isAnnouncementAcknowledgedInSession(announcement.sa_id)) return true
  return false
}

/**
 * Preserve session `isshown` flags across `/api/verify` refreshes —
 * mirrors Angular `UserEffect.verifyOldAnnouncement`.
 *
 * Angular always compares against `getSelectedCurrentUserRole().notification`
 * from localStorage `user`. A prior entry is treated as shown if EITHER Redux
 * or localStorage has `isshown='1'` for that `sa_id`.
 */
export function mergeNotificationShownFlags(
  incoming: RoleSystemAnnouncement[] | undefined,
  previous: RoleSystemAnnouncement[] | undefined,
): RoleSystemAnnouncement[] | undefined {
  if (!incoming?.length) return incoming

  const acked = readAckedIds()
  const stored = readStoredRoleNotifications()

  const wasShownBefore = (saId: number): boolean => {
    const fromRedux = previous?.find((p) => p.sa_id === saId)
    if (fromRedux && isAnnouncementShown(fromRedux)) return true
    const fromStorage = stored?.find((p) => p.sa_id === saId)
    if (fromStorage && isAnnouncementShown(fromStorage)) return true
    return false
  }

  return incoming.map((item) => {
    // Already marked in this payload — keep it (Angular leaves isshown on the item).
    if (isAnnouncementShown(item)) {
      return item
    }
    if (acked.has(item.sa_id) || wasShownBefore(item.sa_id)) {
      return { ...item, isshown: '1' }
    }
    return item
  })
}

/** Read prior notifications from Angular-parity `localStorage.user` when Redux has none. */
export function readStoredRoleNotifications(): RoleSystemAnnouncement[] | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    const user = JSON.parse(raw) as {
      current_role?: { notification?: RoleSystemAnnouncement[] }
    }
    const list = user?.current_role?.notification
    return Array.isArray(list) ? list : undefined
  } catch {
    return undefined
  }
}
